import React from "react";

/**
 * First visible variable cell in render order (standard or transposed hybrid grid),
 * respecting shouldHideCell. When the cell has choices, points at the first choice so parent
 * question TITLE/LABEL attach only to that control (same idea as StarRatingGrid: question context
 * once on first entry, not repeated on every inner control).
 */
export type HybridGridFirstAriaLeadCellCoords = {
    rowIndex: number;
    colIndex: number;
    variableId: number;
    /** Set when the first interactive control in the cell is a choice (radio/checkbox/image). */
    choiceElementId?: string;
};

export const HybridGridFirstAriaLeadCellContext = React.createContext<
    HybridGridFirstAriaLeadCellCoords | null | undefined
>(undefined);

export function useHybridGridFirstAriaLeadCellCoords():
    | HybridGridFirstAriaLeadCellCoords
    | null
    | undefined {
    return React.useContext(HybridGridFirstAriaLeadCellContext);
}

/** Whether this control is the first in-grid focus target that should include the parent question TITLE/LABEL. */
export function matchesHybridGridAriaLeadCell(
    coords: HybridGridFirstAriaLeadCellCoords | null | undefined,
    gridRenderedRowIndex: number | undefined,
    gridRenderedColumnIndex: number | undefined,
    variableId: number | undefined,
    choiceElementId?: string | null,
): boolean | undefined {
    if (coords === undefined) {
        return undefined;
    }
    if (
        gridRenderedRowIndex === undefined ||
        gridRenderedColumnIndex === undefined ||
        variableId === undefined
    ) {
        return undefined;
    }
    if (coords === null) {
        return false;
    }
    if (
        coords.rowIndex !== gridRenderedRowIndex ||
        coords.colIndex !== gridRenderedColumnIndex ||
        coords.variableId !== variableId
    ) {
        return false;
    }
    if (coords.choiceElementId != null && coords.choiceElementId !== "") {
        return choiceElementId === coords.choiceElementId;
    }
    return true;
}

/** Same lead coordinates as {@link matchesHybridGridAriaLeadCell} but for cell-level wrappers (bridge / group), ignoring choice. */
export function matchesHybridGridAriaLeadVariableCell(
    coords: HybridGridFirstAriaLeadCellCoords | null | undefined,
    gridRenderedRowIndex: number | undefined,
    gridRenderedColumnIndex: number | undefined,
    variableId: number | undefined,
): boolean | undefined {
    if (coords === undefined) {
        return undefined;
    }
    if (
        gridRenderedRowIndex === undefined ||
        gridRenderedColumnIndex === undefined ||
        variableId === undefined
    ) {
        return undefined;
    }
    if (coords === null) {
        return false;
    }
    return (
        coords.rowIndex === gridRenderedRowIndex &&
        coords.colIndex === gridRenderedColumnIndex &&
        coords.variableId === variableId
    );
}
